import React, { useContext, useMemo } from 'react';
import { Avatar as MuiAvatar, Box, Typography } from '@material-ui/core';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';

import { ColorThemeContext } from '../../contexts/colorThemeContext';
import { getColor } from '../../theme/styles';
import { ColorThemeType } from '../../types';

// Helper to generate a color from a string
const stringToColor = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += ('00' + value.toString(16)).substr(-2);
  }
  return color;
};

// Helper to get initials from a name
const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

interface Props {
  avatarUrl?: string | null;
  size?: number;
  width?: number | string;
  height?: number;
  style?: React.CSSProperties;
  borderWidth?: number;
  borderColor?: string;
  borderRadius?: number;
  chatType?: string;
  online?: boolean;
  name?: string;
}

const useStyles = makeStyles<
  Theme,
  { size: number; borderWidth: number; borderColor: string; borderRadius: number }
>(() => ({
  root: props => ({
    position: 'relative',
    display: 'inline-block',
    borderWidth: props.borderWidth,
    borderStyle: 'solid',
    borderColor: props.borderColor,
    borderRadius: props.borderRadius,
    width: props.size,
    height: props.size,
    boxSizing: 'border-box',
    overflow: 'visible',
  }),
  avatar: props => ({
    width: props.size,
    height: props.size,
    borderRadius: props.borderRadius - 3,
    fontWeight: 700,
    fontSize: Math.round(props.size * 0.4),
    backgroundColor: 'inherit',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
  }),
  online: props => ({
    position: 'absolute',
    right: 0,
    bottom: 0,
    width: 10,
    height: 10,
    borderRadius: 5,
    border: `2px solid ${props.borderColor}`,
    zIndex: 2,
  }),
}));

const ChatAvatar: React.FC<Props> = ({
  avatarUrl,
  size = 40,
  width,
  height,
  style,
  borderWidth = 2,
  borderRadius = 1000,
  borderColor,
  chatType,
  online,
  name,
}) => {
  const theme = useTheme();
  const themeContext = useContext(ColorThemeContext) as { colorTheme: ColorThemeType };
  const colorTheme = themeContext?.colorTheme || 'light';
  const colors = getColor(colorTheme === 'dark');
  const resolvedBorderColor = borderColor || colors.paperBG;
  const resolvedSize = typeof width === 'number' ? width : size;
  const resolvedHeight = typeof height === 'number' ? height : size;
  const classes = useStyles({
    size: Number(resolvedSize),
    borderWidth,
    borderColor: resolvedBorderColor,
    borderRadius,
  });
  const shouldShowInitials = !avatarUrl && !!name;
  const initials = shouldShowInitials ? getInitials(name!) : '';

  const bgColor = useMemo(() => {
    if (chatType === 'group') {
      return theme.palette.primary.light;
    }

    if (chatType === 'team') {
      return theme.palette.secondary.main;
    }

    if (chatType === 'location') {
      return theme.palette.primary.main;
    }

    return null;
  }, [chatType]);

  const backgroundColor = bgColor || (shouldShowInitials && name ? stringToColor(name) : colors.paperBG);

  return (
    <Box className={classes.root} style={style}>
      {shouldShowInitials ? (
        <Box
          className={classes.avatar}
          style={{
            backgroundColor,
            width: resolvedSize,
            height: resolvedHeight,
            borderRadius: borderRadius - 3,
          }}
        >
          <Typography
            style={{ color: '#fff', fontWeight: 700, fontSize: Math.round(Number(resolvedHeight) * 0.4) }}
          >
            {initials}
          </Typography>
        </Box>
      ) : (
        <MuiAvatar
          src={avatarUrl || ''}
          className={classes.avatar}
          style={{
            width: resolvedSize,
            height: resolvedHeight,
            borderRadius: borderRadius - 3,
            backgroundColor: colors.paperBG,
          }}
          imgProps={{ loading: 'lazy' }}
        >
          {name && !avatarUrl && getInitials(name)}
        </MuiAvatar>
      )}
      {online && (
        <Box
          className={classes.online}
          style={{ backgroundColor: colors.success, borderColor: colors.paperBG }}
        />
      )}
    </Box>
  );
};

export default ChatAvatar;
